import { FUNC_DEFAULT } from 'constants';
import { timer } from 'helpers';
import { useEffect, useRef, useState } from 'react';

const calculateTimeLeft = (target) => {
  const now = new Date().getTime();
  const targetTime = new Date(target).getTime();
  const difference = targetTime - now;

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((difference % (1000 * 60)) / 1000),
    total: difference,
  };
};

const formatTime = (value) => {
  return value.toString().padStart(2, '0');
};

const useCountdownToActionVer3 = ({
  secsInit = 0,
  nowRef = null,
  isLoop = false,
  onComplete = FUNC_DEFAULT,
}) => {
  const millisecondsInit = secsInit * 1000;
  const targetDate = nowRef ? nowRef.current + millisecondsInit : 0;

  const [timeLeft, setTimeLeft] = useState(() => {
    const now = new Date().getTime();
    const targetTime = new Date(targetDate).getTime();
    const difference = targetTime - now;
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
    }
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      ),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000),
      total: difference,
    };
  });
  const [isActive, setIsActive] = useState(true);

  const animationFrameRef = useRef(null);
  const intervalRef = useRef(null);
  const targetDateRef = useRef(targetDate);
  const onCompleteRef = useRef(onComplete);

  const handleOnComplete = (isError = false) => {
    if (onCompleteRef.current) {
      onCompleteRef.current(() => {
        if (isLoop && !isError) {
          setIsActive(true);
          if (nowRef?.current) {
            nowRef.current = Date.now();
          }
        } else {
          console.log(
            `⭐ useCountdownToActionVer3 👉 handleOnComplete ➡️ cleanup ↩`
          );
          cleanup();
        }
      });
    }
  };

  const updateCountdown = () => {
    const newTimeLeft = calculateTimeLeft(targetDateRef.current);
    setTimeLeft(newTimeLeft);
    if (newTimeLeft.total <= 0) {
      setIsActive(false);
      handleOnComplete();
      return;
    }
    if (document.visibilityState === 'visible') {
      animationFrameRef.current = requestAnimationFrame(updateCountdown);
    } else {
      intervalRef.current = setTimeout(updateCountdown, 1000);
    }
  };

  const handleVisibilityChange = (eventName) => {
    if (document.visibilityState === 'visible') {
      console.log(
        `⭐ useCountdownToActionVer3 👉 handleVisibilityChange ➡️ ${eventName} ↩`
      );
      const recalculated = calculateTimeLeft(targetDateRef.current);
      setTimeLeft(recalculated);

      if (recalculated.total <= 0) {
        setIsActive(false);
        handleOnComplete();
        return;
      }

      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }

      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(updateCountdown);
      }
    }
  };

  const handleOnVisibilityChangeListener = () => {
    handleVisibilityChange('visibilitychange');
  };
  const handleOnFocusListener = () => {
    handleVisibilityChange('focus');
  };
  const handleOnBlurListener = () => {
    console.log(`⭐ useCountdownToActionVer3 👉 blur ↩`);
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
    }
    intervalRef.current = setTimeout(updateCountdown, 1000);
  };
  const cleanup = () => {
    targetDateRef.current = 0;
    onCompleteRef.current = null;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
    }
    document.removeEventListener(
      'visibilitychange',
      handleOnVisibilityChangeListener
    );
    window.removeEventListener('focus', handleOnFocusListener);
    document.removeEventListener('focus', handleOnFocusListener);
    window.removeEventListener('blur', handleOnBlurListener);
    document.removeEventListener('blur', handleOnBlurListener);
  };

  useEffect(() => {
    if (!isActive) return;
    targetDateRef.current = targetDate;
    onCompleteRef.current = onComplete;
  }, [isActive, targetDate, onComplete]);

  useEffect(() => {
    const initialTime = calculateTimeLeft(targetDateRef.current);
    setTimeLeft(initialTime);
    if (initialTime.total <= 0) {
      console.log(`⭐ useCountdownToActionVer3 👉 Initial calculation ↩`);
      setIsActive(false);
      handleOnComplete();
      return;
    }

    if (document.visibilityState === 'visible') {
      animationFrameRef.current = requestAnimationFrame(updateCountdown);
    } else {
      intervalRef.current = setTimeout(updateCountdown, 1000);
    }

    // document.addEventListener(
    //   'visibilitychange',
    //   handleOnVisibilityChangeListener
    // );
    window.addEventListener('focus', handleOnFocusListener);
    window.addEventListener('blur', handleOnBlurListener);

    return () => {
      console.log(`⭐ useCountdownToActionVer3 👉 useEffect ➡️ cleanup ↩`);
      cleanup();
    };
  }, [isLoop && targetDate]);

  return {
    days: formatTime(timeLeft.days),
    hours: formatTime(timeLeft.hours),
    minutes: formatTime(timeLeft.minutes),
    seconds: formatTime(timeLeft.seconds),
    totalSeconds: timer.convertMillisecondsToSeconds(timeLeft.total),
  };
};

export default useCountdownToActionVer3;
